import {FC, useEffect, useCallback, useRef, FormEventHandler} from 'react';
import {useApolloClient} from '@apollo/client';
import {
  BroadcastFn,
  extractModuleIdentifiers,
  useParsedText,
  useShowId,
  useShowInstructions,
  useSiteVersionId,
} from '@backstage-components/base';
import {ButtonComponent} from '@backstage-components/button';
import {TextInputComponent} from '@backstage-components/text-input';
import {useMachine} from '@xstate/react';
import {useSubscription} from 'observable-hooks';
import {
  useForm,
  SubmitHandler,
  FieldValues,
  UseFormReturn,
} from 'react-hook-form';
import {AccessCodeContainer} from './AccessCodeContainer';
import {
  defaultFieldData,
  AccessCodeInstructionSchema,
  type AccessCodeComponentDefinition,
} from './AccessCodeDefinition';
import * as Styled from './AccessCode.styled';
import {
  AccessCodeMachine,
  type AccessCodeSuccessEvent,
} from './AccessCodeMachine';
import {AccessCodeViewMachine} from './AccessCodeViewMachine';

export const AccessCodeComponent: FC<AccessCodeComponentDefinition> = (
  definition
) => {
  const {config} = definition;
  const {
    codeTextInputProps,
    showTermsCheckbox = defaultFieldData.showTermsCheckbox,
    accessCodeErrorMessage = defaultFieldData.accessCodeErrorMessage,
    magicLinkSupport,
    magicLinkKey = 'ac',
  } = definition.props;
  const inputName =
    codeTextInputProps?.name ?? defaultFieldData.codeTextInputProps.name;

  const client = useApolloClient();
  const showId = useShowId();
  const siteVersionId = useSiteVersionId();
  const {observable, broadcast} = useShowInstructions(
    AccessCodeInstructionSchema,
    definition
  );
  const [state, dispatch] = useMachine(AccessCodeMachine, {
    input: {broadcast, showId},
  });
  const [viewState, viewDispatch] = useMachine(AccessCodeViewMachine, {
    input: {broadcast, client, siteVersionId},
  });
  const form = useForm({
    /**
     * @param mode defines when validation is triggered.
     * AccessCode is set at 'onSubmit'
     */
    mode: 'onSubmit',
  });
  const {handleSubmit, setError, setValue, getValues, clearErrors} = form;

  useEffect(() => {
    if (viewState.matches('resendSuccess')) {
      // clear values if resend was successful
      setValue(inputName, '');
    }
  }, [inputName, setValue, viewState]);

  useEffect(() => {
    if (state.matches('failure') && 'reason' in state.context) {
      setError(inputName, {
        message: state.context.reason || accessCodeErrorMessage,
      });
    }
  }, [accessCodeErrorMessage, inputName, setError, state]);
  useSubscription(observable, {
    next: (instruction) => dispatch(instruction),
  });
  useSubscription(observable, {
    next: (instruction) => viewDispatch(instruction),
  });

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined = undefined;
    const onAccessCodeSuccess = (e: AccessCodeSuccessEvent): void => {
      const {detail} = e;
      timeoutId = setTimeout(() => {
        broadcast({
          type: 'AccessCode:on-success',
          meta: {
            showId: detail.showId,
            attendeeId: detail.attendee.id,
            attendeeEmail: detail.attendee.email,
            attendeeName: detail.attendee.name,
            attendeeTags: detail.attendee.tags.join(','),
          },
        });
      }, 50);
    };
    document.body.addEventListener('AccessCode:success', onAccessCodeSuccess);
    return () => {
      document.body.removeEventListener(
        'AccessCode:success',
        onAccessCodeSuccess
      );
      clearTimeout(timeoutId);
    };
  }, [broadcast]);

  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    (data) => {
      // Send the instruction to the state machine, which will trigger the
      // broadcast
      if (typeof data[inputName] !== 'string') {
        return;
      }
      broadcast({
        type: 'AccessCode:verify',
        meta: {accessCode: data[inputName].toUpperCase(), showId},
      });
    },
    [broadcast, inputName, showId]
  );

  const hasAutoSubmitExecuted = useRef(false);

  useEffect(() => {
    if (
      !window?.location ||
      config.scope !== 'attendee' ||
      magicLinkSupport === 'none' ||
      hasAutoSubmitExecuted.current === true
    ) {
      return;
    }

    // ensure effect only runs once
    hasAutoSubmitExecuted.current = true;

    if (
      magicLinkSupport === 'auto-fill' ||
      magicLinkSupport === 'auto-submit'
    ) {
      const inputKey = defaultFieldData.codeTextInputProps?.name;
      const query = window.location.search;
      const params = new URLSearchParams(query);
      const ac = params.get(magicLinkKey) ?? '';

      if (getValues(inputKey) === '') {
        setValue(inputKey, ac);
      }
      if (
        ac.length > 0 &&
        !showTermsCheckbox &&
        magicLinkSupport === 'auto-submit'
      ) {
        handleSubmit(onSubmit)();
      }
    }
  }, [
    config.scope,
    getValues,
    handleSubmit,
    magicLinkKey,
    magicLinkSupport,
    onSubmit,
    setValue,
    showTermsCheckbox,
  ]);

  if (
    viewState.matches('login') ||
    viewState.matches('loginPending') ||
    viewState.matches('resendSuccess')
  ) {
    return (
      <VerifyAccessCode
        {...definition}
        broadcast={broadcast}
        error={viewState.context.error ?? undefined}
        form={form}
        isLoading={viewState.value === 'loginPending'}
        isResendSuccess={viewState.value === 'resendSuccess'}
        onSubmit={onSubmit}
        clearErrors={clearErrors}
      />
    );
  } else if (
    viewState.matches('resend') ||
    viewState.matches('resendPending')
  ) {
    const resendEmailNotFoundErrorMessage =
      viewState.context.code === 'AttendeeNotFound' &&
      viewState.context.error !== null
        ? definition.props.resendAccessCodeFormContent?.emailErrorMessage
        : undefined;
    return (
      <ResendAccessCode
        {...definition}
        broadcast={broadcast}
        error={
          viewState.context.error
            ? resendEmailNotFoundErrorMessage ?? viewState.context.error
            : undefined
        }
        isLoading={viewState.value === 'resendPending'}
        showId={showId}
        clearErrors={clearErrors}
      />
    );
  } else {
    // Access Code verification success
    return null;
  }
};

interface VerifyAccessCodeProps extends AccessCodeComponentDefinition {
  broadcast: BroadcastFn<typeof AccessCodeInstructionSchema>;
  error?: string;
  form: Pick<
    UseFormReturn<FieldValues>,
    'formState' | 'handleSubmit' | 'register'
  >;
  isLoading?: boolean;
  isResendSuccess?: boolean;
  onSubmit: SubmitHandler<FieldValues>;
  clearErrors: () => void;
}

/**
 * The view to display when the component is in the "verify" view state
 * @private exported for tests
 */
export const VerifyAccessCode: FC<VerifyAccessCodeProps> = (props) => {
  const {
    broadcast,
    error,
    form,
    isLoading = false,
    isResendSuccess = false,
    onSubmit,
    clearErrors,
    ...definition
  } = props;
  const moduleIdentifiers = extractModuleIdentifiers(definition);

  const {id, mid, config} = definition;
  const checkboxId = `terms-checkbox-${id}`;
  const {
    title,
    titleColor,
    subtitle,
    subtitleColor,
    codeTextInputProps,
    termsLinkProps,
    resendButtonProps,
    submitButtonProps,
    resendAccessCodeFormContent,
    showResendLink = defaultFieldData.showResendLink,
    showTermsCheckbox = defaultFieldData.showTermsCheckbox,
    validationMessages,
  } = definition.props;
  const inputName =
    codeTextInputProps?.name ?? defaultFieldData.codeTextInputProps.name;

  const validationOptions = {
    required:
      validationMessages?.accessCodeRequired ??
      defaultFieldData.validationMessages.accessCodeRequired,
  };

  const {register, formState, handleSubmit} = form;
  const errors = formState.errors;
  const termsError = errors.terms?.message;
  const accessCodeError = errors[inputName]?.message;
  const errorMessage =
    typeof error === 'string'
      ? error
      : typeof termsError === 'string'
      ? termsError
      : typeof accessCodeError === 'string'
      ? accessCodeError
      : undefined;

  const termsLinkContent = useParsedText(
    termsLinkProps?.content ?? 'No content for terms link provided'
  );
  const successMessage =
    resendAccessCodeFormContent?.successMessage ??
    defaultFieldData.resendAccessCodeFormContent.successMessage;

  return (
    <AccessCodeContainer
      id={id}
      className="access-code-wrapper access-code-verify"
      definition={definition}
    >
      <Styled.Title className="access-code-title" titleColor={titleColor}>
        {title || defaultFieldData.title}
      </Styled.Title>
      <Styled.Subtitle
        className="access-code-subtitle"
        subtitleColor={subtitleColor}
      >
        {subtitle || defaultFieldData.subtitle}
      </Styled.Subtitle>
      <Styled.Form
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit)}
      >
        {termsLinkProps && showTermsCheckbox && (
          <Styled.CheckboxInputGroup className="access-code-checkbox-input-group">
            <Styled.Checkbox
              id={checkboxId}
              role="checkbox"
              className="access-code-checkbox-input"
              type="checkbox"
              {...register('terms', {
                required:
                  validationMessages?.checkboxRequired ||
                  defaultFieldData.validationMessages.checkboxRequired,
              })}
            />
            <Styled.CheckboxLabel
              htmlFor={checkboxId}
              className="access-code-checkbox-label"
            >
              {termsLinkContent}
            </Styled.CheckboxLabel>
          </Styled.CheckboxInputGroup>
        )}
        <Styled.CodeInputGroup className="access-code-text-input-group">
          <TextInputComponent
            config={config}
            {...moduleIdentifiers}
            mid={`${mid}-access-code-text-input`}
            id={`${id}-access-code-text-input`}
            component={'TextInput'}
            props={{
              disabled: isLoading,
              parentRegister: register,
              name: defaultFieldData.codeTextInputProps.name,
              inputType: defaultFieldData.codeTextInputProps.inputType,
              ...codeTextInputProps,
              validationOptions: validationOptions ?? {
                required:
                  defaultFieldData.validationMessages.accessCodeRequired,
              },
              styleAttr: `${Styled.codeTextInputStyle}
              ${codeTextInputProps?.styleAttr}`,
            }}
          />
          {!(
            defaultFieldData.submitButtonProps &&
            'href' in defaultFieldData.submitButtonProps
          ) && (
            <ButtonComponent
              config={config}
              {...moduleIdentifiers}
              mid={`${mid}-access-code-submit-button`}
              id={`${id}-button`}
              component={'Button'}
              props={{
                ...submitButtonProps,
                disabled: isLoading,
                isLoading,
                children:
                  submitButtonProps?.children ||
                  defaultFieldData.submitButtonProps?.children,
                border: defaultFieldData.submitButtonProps?.border,
                htmlType: 'submit',
              }}
            />
          )}
        </Styled.CodeInputGroup>

        <SuccessMessage
          className="access-code-text-input-success-message"
          message={isResendSuccess ? successMessage : undefined}
        />
        <ErrorMessage
          className="access-code-text-input-error-message"
          error={errorMessage}
        />
      </Styled.Form>

      {resendButtonProps && showResendLink && (
        <div className="resend-button-component">
          <ButtonComponent
            config={config}
            {...moduleIdentifiers}
            id={`${id}-access-code-resend-button`}
            mid={`${mid}-access-code-resend-button`}
            component={'Button'}
            props={{
              onClick: () => {
                clearErrors();
                broadcast({type: 'AccessCode:resend-view', meta: {}});
              },
              ...resendButtonProps,
              background: {buttonColor: 'transparent'},
              children:
                resendButtonProps.children ||
                defaultFieldData.resendButtonProps?.children,
              typography:
                resendButtonProps.typography ||
                defaultFieldData.resendButtonProps?.typography,
            }}
          />
        </div>
      )}
    </AccessCodeContainer>
  );
};

interface ResendAccessCodeProps extends AccessCodeComponentDefinition {
  broadcast: BroadcastFn<typeof AccessCodeInstructionSchema>;
  error?: string;
  isLoading?: boolean;
  showId: string;
  clearErrors: () => void;
}

/**
 * The view to display when the component is in the "resend" view state
 * @private exported for tests
 */
export const ResendAccessCode: FC<ResendAccessCodeProps> = (props) => {
  const {
    broadcast,
    error,
    isLoading = false,
    showId,
    clearErrors,
    ...definition
  } = props;
  const {id, mid, config} = definition;
  const moduleIdentifiers = extractModuleIdentifiers(definition);
  const {cid: coreId} = moduleIdentifiers;
  const formRef = useRef<HTMLFormElement | null>(null);
  const {
    codeTextInputProps,
    resendAccessCodeFormContent,
    resendButtonProps,
    submitButtonProps,
    validationMessages,
    titleColor,
    subtitleColor,
  } = definition.props;
  const handleSubmit: FormEventHandler = useCallback(
    (e) => {
      e.preventDefault();
      if (!formRef.current) {
        return;
      }
      const data = new FormData(formRef.current);
      const email = data.get('email');
      if (typeof email !== 'string' || email === '') {
        broadcast({
          type: 'AccessCode:on-resend-failure',
          meta: {
            reason:
              validationMessages?.emailRequired ?? 'Email must be provided',
          },
        });
      } else {
        broadcast({
          type: 'AccessCode:resend',
          meta: {coreId, email, environmentId: showId},
        });
      }
    },
    [broadcast, coreId, showId, validationMessages?.emailRequired]
  );
  const backLabel =
    resendAccessCodeFormContent?.backButtonText ??
    defaultFieldData.resendAccessCodeFormContent.backButtonText;
  const title =
    resendAccessCodeFormContent?.title ??
    defaultFieldData.resendAccessCodeFormContent.title;
  const subtitle =
    resendAccessCodeFormContent?.subtitle ??
    defaultFieldData.resendAccessCodeFormContent.subtitle;
  const submitLabel =
    resendAccessCodeFormContent?.submitButtonLabel ??
    defaultFieldData.resendAccessCodeFormContent.submitButtonLabel;
  const placeholder =
    resendAccessCodeFormContent?.placeholder ??
    defaultFieldData.resendAccessCodeFormContent.placeholder;

  return (
    <AccessCodeContainer
      id={id}
      definition={definition}
      className="access-code-wrapper access-code-resend"
    >
      <Styled.Title className="access-code-title" titleColor={titleColor}>
        {title}
      </Styled.Title>
      <Styled.Subtitle
        className="access-code-subtitle"
        subtitleColor={subtitleColor}
      >
        {subtitle}
      </Styled.Subtitle>
      <Styled.Form ref={formRef} autoComplete="off" onSubmit={handleSubmit}>
        <Styled.CodeInputGroup className="access-code-text-input-group">
          <TextInputComponent
            config={config}
            {...moduleIdentifiers}
            id={`${id}-access-code-text-input`}
            mid={`${mid}-access-code-text-input`}
            component="TextInput"
            props={{
              ...codeTextInputProps,
              disabled: isLoading,
              name: 'email',
              inputType: 'text',
              placeholder: placeholder,
              styleAttr: `${Styled.codeTextInputStyle}
              ${codeTextInputProps?.styleAttr}`,
            }}
          />
          {!(
            defaultFieldData.submitButtonProps &&
            'href' in defaultFieldData.submitButtonProps
          ) && (
            <ButtonComponent
              config={config}
              {...moduleIdentifiers}
              id={`${id}-access-code-submit-button`}
              mid={`${mid}-access-code-submit-button`}
              component={'Button'}
              props={{
                ...submitButtonProps,
                disabled: isLoading,
                isLoading,
                children: submitLabel,
                border: defaultFieldData.submitButtonProps?.border,
                htmlType: 'submit',
              }}
            />
          )}
        </Styled.CodeInputGroup>
        <ErrorMessage
          className="access-code-text-input-error-message"
          error={error}
        />
      </Styled.Form>
      <div className="resend-button-component">
        <ButtonComponent
          config={config}
          {...moduleIdentifiers}
          id={`${id}-access-code-resend-button`}
          mid={`${mid}-access-code-resend-button`}
          component={'Button'}
          props={{
            onClick: () => {
              clearErrors();
              broadcast({type: 'AccessCode:login-view', meta: {}});
            },
            ...resendButtonProps,
            background: {buttonColor: 'transparent'},
            children: backLabel,
            typography:
              resendButtonProps?.typography ||
              defaultFieldData.resendButtonProps?.typography,
          }}
        />
      </div>
    </AccessCodeContainer>
  );
};

/**
 * Displays a stylized error message if an `error` string is provided.
 * @private exported for tests
 */
export const ErrorMessage: FC<{className: string; error?: string}> = (
  props
) => {
  const {className, error} = props;
  if (error) {
    return (
      <Styled.ErrorMessage data-testid={className} className={className}>
        {error}
      </Styled.ErrorMessage>
    );
  } else {
    return null;
  }
};

/**
 * Displays a stylized error message if a `message` string is provided.
 * @private exported for tests
 */
export const SuccessMessage: FC<{className: string; message?: string}> = (
  props
) => {
  const {className, message} = props;
  if (message) {
    return (
      <Styled.SuccessMessage data-testid={className} className={className}>
        {message}
      </Styled.SuccessMessage>
    );
  } else {
    return null;
  }
};
